import { supabase } from '../lib/supabase';

export const carHouseService = {
  // Get all car houses with optional filters
  async getAllCarHouses(filters = {}) {
    try {
      let query = supabase?.from('car_houses')?.select('*')?.order('name');

      // Apply search filter
      if (filters?.search) {
        query = query?.or(`name.ilike.%${filters?.search}%,city.ilike.%${filters?.search}%,contact_person.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
      }

      // Apply status filter
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Get order statistics for each car house
      const carHousesWithStats = await Promise.all(
        data?.map(async (carHouse) => {
          try {
            const { data: stats } = await this.getCarHouseStats(carHouse.id);
            return {
              ...carHouse,
              stats: stats || {
                activeOrders: 0,
                completedOrders: 0,
                totalRevenue: 0,
                averageRating: 0
              }
            };
          } catch (statsError) {
            console.error(`Error getting stats for car house ${carHouse.id}:`, statsError);
            return {
              ...carHouse,
              stats: {
                activeOrders: 0,
                completedOrders: 0,
                totalRevenue: 0,
                averageRating: 0
              }
            };
          }
        }) || []
      );

      return { data: carHousesWithStats, error: null };
    } catch (error) {
      console.error('Error fetching car houses:', error);
      return { data: null, error };
    }
  },

  // Get car house by ID
  async getCarHouseById(id) {
    try {
      const { data, error } = await supabase?.from('car_houses')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching car house:', error);
      return { data: null, error };
    }
  },

  // Create new car house
  async createCarHouse(carHouseData) {
    try {
      // Generate a unique code for the car house
      const code = await this.generateUniqueCode(carHouseData.name);

      const carHousePayload = {
        name: carHouseData.name,
        code: code,
        address: carHouseData.street || '',
        city: carHouseData.city,
        state: 'Nordrhein-Westfalen', // Default state
        zip_code: carHouseData.zipCode,
        country: 'United States',
        phone: carHouseData.phone,
        email: carHouseData.email,
        website: null,
        contact_person: carHouseData.contactPerson,
        contact_phone: carHouseData.phone,
        manager_id: null,
        status: 'active', // Default status
        settings: {
          vat_id: carHouseData.vatId,
          notes: carHouseData.notes
        }
      };

      const { data, error } = await supabase?.from('car_houses')?.insert(carHousePayload)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error creating car house:', error);
      return { data: null, error };
    }
  },

  // Update car house
  async updateCarHouse(id, carHouseData) {
    try {
      const carHousePayload = {
        name: carHouseData.name,
        address: carHouseData.street || '',
        city: carHouseData.city,
        zip_code: carHouseData.zipCode,
        phone: carHouseData.phone,
        email: carHouseData.email,
        contact_person: carHouseData.contactPerson,
        contact_phone: carHouseData.phone,
        settings: {
          vat_id: carHouseData.vatId,
          notes: carHouseData.notes
        },
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase?.from('car_houses')?.update(carHousePayload)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating car house:', error);
      return { data: null, error };
    }
  },

  // Delete car house
  async deleteCarHouse(id) {
    try {
      const { error } = await supabase?.from('car_houses')?.delete()?.eq('id', id);

      if (error) throw error;

      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting car house:', error);
      return { data: null, error };
    }
  },

  // Bulk delete car houses
  async bulkDeleteCarHouses(ids) {
    try {
      const { error } = await supabase?.from('car_houses')?.delete()?.in('id', ids);

      if (error) throw error;

      return { data: true, error: null };
    } catch (error) {
      console.error('Error bulk deleting car houses:', error);
      return { data: null, error };
    }
  },

  // Generate unique code for car house
  async generateUniqueCode(name) {
    try {
      // Create a base code from the name
      const baseCode = name
        .replace(/[^a-zA-Z0-9]/g, '')
        .substring(0, 3)
        .toUpperCase();

      // Check if code exists
      const { data: existing } = await supabase
        ?.from('car_houses')
        ?.select('code')
        ?.ilike('code', `${baseCode}%`)
        ?.order('code', { ascending: false })
        ?.limit(1);

      if (!existing || existing.length === 0) {
        return `${baseCode}001`;
      }

      // Extract number from existing code and increment
      const lastCode = existing[0].code;
      const match = lastCode.match(new RegExp(`^${baseCode}(\\d+)$`));
      
      if (match) {
        const number = parseInt(match[1]) + 1;
        return `${baseCode}${number.toString().padStart(3, '0')}`;
      }

      // Fallback: add timestamp
      return `${baseCode}${Date.now().toString().slice(-3)}`;
    } catch (error) {
      console.error('Error generating unique code:', error);
      // Fallback: use timestamp
      return `CH${Date.now().toString().slice(-6)}`;
    }
  },

  // Map status to database enum
  mapStatusToEnum(status) {
    const statusMap = {
      'Aktiv': 'active',
      'Inaktiv': 'inactive',
      'Wartung': 'maintenance'
    };
    return statusMap[status] || 'active';
  },

  // Map database enum to display status
  mapEnumToStatus(enumValue) {
    const statusMap = {
      'active': 'Aktiv',
      'inactive': 'Inaktiv',
      'maintenance': 'Wartung'
    };
    return statusMap[enumValue] || 'Aktiv';
  },

  // Transform database data to frontend format
  transformToFrontendFormat(dbData) {
    if (!dbData) return null;

    return {
      id: dbData.id,
      name: dbData.name,
      code: dbData.code,
      street: dbData.address || '',
      zipCode: dbData.zip_code || '',
      city: dbData.city,
      contactPerson: dbData.contact_person || '',
      email: dbData.email || '',
      phone: dbData.phone || '',
      vatId: dbData.settings?.vat_id || '',
      notes: dbData.settings?.notes || '',
      activeOrders: dbData.stats?.activeOrders || 0,
      completedOrders: dbData.stats?.completedOrders || 0,
      totalRevenue: dbData.stats?.totalRevenue || 0,
      averageRating: dbData.stats?.averageRating || 0,
      createdAt: dbData.created_at,
      updatedAt: dbData.updated_at
    };
  },

  // Transform frontend data to database format
  transformToDatabaseFormat(frontendData) {
    return {
      name: frontendData.name,
      address: frontendData.street || '',
      city: frontendData.city,
      zip_code: frontendData.zipCode,
      phone: frontendData.phone,
      email: frontendData.email,
      contact_person: frontendData.contactPerson,
      contact_phone: frontendData.phone,
      settings: {
        vat_id: frontendData.vatId,
        notes: frontendData.notes
      }
    };
  },

  // Get car house statistics
  async getCarHouseStats(carHouseId) {
    try {
      
      // Get active orders count
      const { data: activeOrders, error: activeError } = await supabase
        ?.from('orders')
        ?.select('id')
        ?.eq('car_house_id', carHouseId)
        ?.in('status', ['pending', 'in_progress']);

      if (activeError) throw activeError;

      // Get completed orders count
      const { data: completedOrders, error: completedError } = await supabase
        ?.from('orders')
        ?.select('id')
        ?.eq('car_house_id', carHouseId)
        ?.eq('status', 'completed');

      if (completedError) throw completedError;

      // Get total revenue from completed orders with service items
      const { data: revenueData, error: revenueError } = await supabase
        ?.from('orders')
        ?.select(`
          id,
          total_amount,
          order_service_items(
            quantity,
            unit_price,
            total_price,
            service_item:service_items(
              base_price,
              service_type:service_types(price)
            )
          )
        `)
        ?.eq('car_house_id', carHouseId)
        ?.eq('status', 'completed');

      if (revenueError) throw revenueError;

      // Get average rating from completed orders
      const { data: ratingData, error: ratingError } = await supabase
        ?.from('orders')
        ?.select('rating')
        ?.eq('car_house_id', carHouseId)
        ?.eq('status', 'completed')
        ?.not('rating', 'is', null);

      if (ratingError) throw ratingError;

      // Calculate totals with detailed logging
      const totalRevenue = revenueData?.reduce((sum, order) => {
        // First try to use the order's total_amount if it exists
        if (order.total_amount && order.total_amount > 0) {
          return sum + order.total_amount;
        }
        
        // Otherwise calculate from service items
        const orderRevenue = order.order_service_items?.reduce((itemSum, item) => {
          
          // Use total_price if available, otherwise calculate from unit_price and quantity
          if (item.total_price && item.total_price > 0) {
            return itemSum + item.total_price;
          } else if (item.unit_price && item.quantity) {
            const calculated = item.unit_price * item.quantity;
            return itemSum + calculated;
          } else if (item.service_item?.base_price) {
            const calculated = item.service_item.base_price * (item.quantity || 1);
            return itemSum + calculated;
          } else if (item.service_item?.service_type?.price) {
            const calculated = item.service_item.service_type.price * (item.quantity || 1);
            return itemSum + calculated;
          }
          return itemSum;
        }, 0) || 0;
        
        return sum + orderRevenue;
      }, 0) || 0;
      
      const averageRating = ratingData?.length > 0 
        ? (ratingData.reduce((sum, order) => sum + (order.rating || 0), 0) / ratingData.length).toFixed(1)
        : 0;

      const result = {
        activeOrders: activeOrders?.length || 0,
        completedOrders: completedOrders?.length || 0,
        totalRevenue: totalRevenue,
        averageRating: averageRating
      };


      return {
        data: result,
        error: null
      };
    } catch (error) {
      console.error('Error fetching car house stats:', error);
      return {
        data: {
          activeOrders: 0,
          completedOrders: 0,
          totalRevenue: 0,
          averageRating: 0
        },
        error
      };
    }
  },

  // Manual function to recalculate totals for all completed orders
  async recalculateCompletedOrderTotals(carHouseId) {
      try {
      
      // Get all completed orders for this car house
      const { data: completedOrders, error } = await supabase
        ?.from('orders')
        ?.select('id')
        ?.eq('car_house_id', carHouseId)
        ?.eq('status', 'completed');

      if (error) throw error;

      // Recalculate totals for each order
      for (const order of completedOrders || []) {
        await supabase?.rpc('calculate_order_totals', { order_uuid: order.id });
      }

      return { data: true, error: null };
    } catch (error) {
      console.error('Error recalculating order totals:', error);
      return { data: null, error };
    }
  },

  // Debug function to check order details
  async debugOrderDetails(carHouseId) {
    try {
      
      // Get all orders for this car house
      const { data: orders, error: ordersError } = await supabase
        ?.from('orders')
        ?.select(`
          id,
          order_number,
          status,
          total_amount,
          customer_name,
          title,
          created_at
        `)
        ?.eq('car_house_id', carHouseId);

      if (ordersError) throw ordersError;


      // Check service items for each order
      for (const order of orders || []) {
        const { data: serviceItems, error: itemsError } = await supabase
          ?.from('order_service_items')
          ?.select(`
            id,
            quantity,
            unit_price,
            total_price,
            service_item:service_items(
              name,
              base_price,
              service_type:service_types(name, price)
            )
          `)
          ?.eq('order_id', order.id);

        if (itemsError) {
        } else {
        }
      }

      return { data: { orders, error: null } };
    } catch (error) {
      console.error('Error debugging order details:', error);
      return { data: null, error };
    }
  },

  // Function to add a default service item to orders without service items
  async addDefaultServiceItemToOrder(orderId, serviceTypeId = null) {
    try {
      
      // First, get a default service item
      let serviceItemId;
      
      if (serviceTypeId) {
        // Use the provided service type
        const { data: serviceItem, error: itemError } = await supabase
          ?.from('service_items')
          ?.select('id')
          ?.eq('service_type_id', serviceTypeId)
          ?.limit(1)
          ?.single();
          
        if (itemError) throw itemError;
        serviceItemId = serviceItem?.id;
      } else {
        // Get the first available service item
        const { data: serviceItem, error: itemError } = await supabase
          ?.from('service_items')
          ?.select('id')
          ?.limit(1)
          ?.single();
          
        if (itemError) throw itemError;
        serviceItemId = serviceItem?.id;
      }

      if (!serviceItemId) {
        throw new Error('No service items available');
      }

      // Add the service item to the order
      const { data, error } = await supabase
        ?.from('order_service_items')
        ?.insert({
          order_id: orderId,
          service_item_id: serviceItemId,
          quantity: 1,
          unit_price: 0, // Will be calculated from service item
          total_price: 0  // Will be calculated
        })
        ?.select()
        ?.single();

      if (error) throw error;

      
      // Recalculate order totals
      await supabase?.rpc('calculate_order_totals', { order_uuid: orderId });

      return { data, error: null };
    } catch (error) {
      console.error('Error adding default service item:', error);
      return { data: null, error };
    }
  }
};
